import { useRef } from "react";
import PropTypes from "prop-types";

const OTP_LENGTH = 6;

const OtpInput = ({ value, onChange, length = OTP_LENGTH, className = "" }) => {
  const inputRefs = useRef([]);

  const applyDigits = (raw, startIndex) => {
    const digits = raw.replace(/\D/g, "").split("");
    if (!digits.length) return;

    const newOtp = [...value];
    let nextIndex = startIndex;

    for (const digit of digits) {
      if (nextIndex >= length) break;
      newOtp[nextIndex] = digit;
      nextIndex += 1;
    }

    onChange(newOtp);
    inputRefs.current[Math.min(nextIndex, length - 1)]?.focus();
  };

  const handleChange = (index, inputValue) => {
    if (!/^\d*$/.test(inputValue)) return;

    // Paste/autofill can deliver multiple characters in a single change event.
    if (inputValue.length > 1) {
      applyDigits(inputValue, index);
      return;
    }

    const newOtp = [...value];
    newOtp[index] = inputValue;
    onChange(newOtp);

    if (inputValue && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (value[index]) {
        const newOtp = [...value];
        newOtp[index] = "";
        onChange(newOtp);
        return;
      }

      if (index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    }
  };

  const handlePaste = (e, startIndex) => {
    e.preventDefault();
    const text = e.clipboardData?.getData("text") ?? "";
    applyDigits(text, startIndex);
  };

  return (
    <div className={`flex justify-between mb-6 ${className}`}>
      {value.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? "one-time-code" : "off"}
          autoFocus={index === 0}
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={(e) => handlePaste(e, index)}
          aria-label={`OTP digit ${index + 1}`}
          className="w-12 h-12 text-2xl text-center border-2 rounded-md border-grey focus:border-primary focus:outline-none preserve-text-black"
        />
      ))}
    </div>
  );
};

OtpInput.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  length: PropTypes.number,
  className: PropTypes.string,
};

export default OtpInput;
